import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sun, Moon, Menu, X } from 'lucide-react';
import { DEFAULT_CONTENT, HubFooter } from './HubSections';

const API = process.env.REACT_APP_BACKEND_URL;

export function Logo() {
  return (
    <span className="inline-flex items-center gap-2" data-testid="hub-logo">
      <img src="/cb-logo.png" alt="CountBucks logo" className="h-8 w-8 sm:h-9 sm:w-9" />
      <span className="text-sm font-extrabold tracking-[0.1em] text-[#118A35] sm:text-lg sm:tracking-[0.18em] dark:text-[#27B84A]">COUNTBUCKS</span>
    </span>
  );
}

export function HubHeader({ nav, dk, onToggleTheme, onAnchor }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-[#E4E8E6] bg-white/90 backdrop-blur-md dark:border-[#1C2420] dark:bg-[#0A0E0C]/90" data-testid="hub-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" aria-label="CountBucks home"><Logo /></Link>
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Main navigation">
          {nav.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => onAnchor(e, item.href)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="text-sm font-medium text-[#596273] transition-colors hover:text-[#18202D] dark:text-[#8B948F] dark:hover:text-white"
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={onToggleTheme}
            aria-label={dk ? 'Switch to light mode' : 'Switch to dark mode'}
            data-testid="theme-toggle"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E4E8E6] text-[#596273] transition-colors hover:text-[#FF7816] sm:h-10 sm:w-10 dark:border-[#1C2420] dark:text-[#8B948F] dark:hover:text-[#FF7816]"
          >
            {dk ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link
            to="/login"
            data-testid="header-login-btn"
            className="hidden rounded-xl border border-[#E4E8E6] px-4 py-2 text-sm font-semibold text-[#18202D] transition-colors hover:border-[#27B84A] sm:block dark:border-[#1C2420] dark:text-white"
          >
            Login
          </Link>
          <Link
            to="/login?mode=register&plan=starter&cadence=monthly"
            data-testid="header-trial-btn"
            className="whitespace-nowrap rounded-xl bg-[#27B84A] px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#118A35] sm:px-4 sm:text-sm"
          >
            Start Free Trial
          </Link>
          <button
            type="button"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[#18202D] lg:hidden dark:text-white"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-[#E4E8E6] bg-white px-6 py-4 lg:hidden dark:border-[#1C2420] dark:bg-[#0A0E0C]" data-testid="mobile-menu" aria-label="Mobile navigation">
          {nav.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => { setOpen(false); onAnchor(e, item.href); }}
              className="block py-3 text-base font-medium text-[#18202D] dark:text-white"
            >
              {item.label}
            </a>
          ))}
          <Link to="/login" className="block py-3 text-base font-medium text-[#596273] dark:text-[#8B948F]">Login</Link>
        </nav>
      )}
    </header>
  );
}

export function useMarketingTheme() {
  const [dk, setDk] = useState(() => localStorage.getItem('cb-home-theme') === 'dark');
  const toggleTheme = () => {
    const next = !dk;
    setDk(next);
    localStorage.setItem('cb-home-theme', next ? 'dark' : 'light');
  };
  return { dk, toggleTheme };
}

export function useHomepageContent() {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  useEffect(() => {
    fetch(`${API}/api/public/homepage`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.hero) setContent({ ...DEFAULT_CONTENT, ...d }); })
      .catch(() => {});
  }, []);
  return content;
}

export default function MarketingShell({ children, title }) {
  const navigate = useNavigate();
  const content = useHomepageContent();
  const { dk, toggleTheme } = useMarketingTheme();

  useEffect(() => {
    if (title) document.title = title;
    window.scrollTo(0, 0);
  }, [title]);

  const onAnchor = (e, href) => {
    e.preventDefault();
    if (href.startsWith('#')) navigate(`/${href}`);
    else if (href.startsWith('/')) navigate(href);
  };

  return (
    <div className={dk ? 'dark' : ''}>
      <div className="min-h-screen overflow-x-hidden bg-white font-sans text-[#18202D] antialiased dark:bg-[#0A0E0C] dark:text-[#F2F5F3]" data-testid="marketing-shell">
        <HubHeader nav={content.nav} dk={dk} onToggleTheme={toggleTheme} onAnchor={onAnchor} />
        {children}
        <HubFooter footer={content.footer} onAnchor={onAnchor} />
      </div>
    </div>
  );
}
